/**
 * Social Map viewport discovery 模型（MAP-SCALE-B）。
 * marker 來源＝可視範圍地點聚合 API（socialMapApi.listLocations）；marker 身分＝locationId（十進位字串），
 * marker 數字＝backend matchingPostCount，不得由已載入貼文推算。
 */

export const SOCIAL_MAP_VIEWPORT_LIMIT = 200;
export const SOCIAL_MAP_ZOOM_MIN = 0;
export const SOCIAL_MAP_ZOOM_MAX = 22;
export const SOCIAL_MAP_LOCATION_KINDS = Object.freeze([
  'ALL', 'MOOD', 'JOURNEY', 'JOURNEY_INVITATION', 'PLACE', 'FOOD',
]);
export const SOCIAL_MAP_COUNT_DISPLAY_CAP = 99;
/** 發布時間篩選（backend 以 UTC published_at 判斷）；ALL＝不限時間。 */
export const SOCIAL_MAP_TIME_RANGES = Object.freeze(['ALL', 'LAST_24_HOURS', 'LAST_7_DAYS', 'LAST_30_DAYS']);
export const SOCIAL_MAP_TIME_RANGE_LABELS = Object.freeze({
  ALL: '不限時間',
  LAST_24_HOURS: '最近 24 小時',
  LAST_7_DAYS: '最近 7 天',
  LAST_30_DAYS: '最近 30 天',
});

export const SOCIAL_MAP_VIEWPORT_STORAGE_KEY = 'formosa.travel.socialMap.viewport';
export const SOCIAL_MAP_VIEWPORT_SCHEMA_VERSION = 1;

/** 量化到 1e-5 度（約 1 公尺）：同一畫面重複 idle 會得到相同 key，不同區域不會被合併。 */
const COORDINATE_PRECISION = 5;

/** 台灣預設鏡頭；bounds 只供無互動地圖（fallback）時作為初次查詢範圍。 */
export const TAIWAN_DEFAULT_CAMERA = Object.freeze({
  source: 'TAIWAN_DEFAULT',
  center: Object.freeze({ lat: 23.7, lng: 121.0 }),
  zoom: 7,
  bounds: Object.freeze({ north: 25.6, south: 21.7, east: 122.4, west: 119.6 }),
});

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function quantize(value) {
  return Number(value.toFixed(COORDINATE_PRECISION));
}

export function normalizeSocialMapKind(kind) {
  const value = String(kind || 'ALL').toUpperCase();
  return SOCIAL_MAP_LOCATION_KINDS.includes(value) ? value : 'ALL';
}

export function normalizeSocialMapTimeRange(timeRange) {
  const value = String(timeRange || 'ALL').toUpperCase();
  return SOCIAL_MAP_TIME_RANGES.includes(value) ? value : 'ALL';
}

export function socialMapTimeRangeLabel(timeRange) {
  return SOCIAL_MAP_TIME_RANGE_LABELS[normalizeSocialMapTimeRange(timeRange)];
}

function normalizeZoom(zoom) {
  if (!isFiniteNumber(zoom)) return null;
  const rounded = Math.round(zoom);
  if (rounded < SOCIAL_MAP_ZOOM_MIN || rounded > SOCIAL_MAP_ZOOM_MAX) return null;
  return rounded;
}

/**
 * 驗證並量化 viewport；規則與 backend SocialMapViewport 相同。
 * east < west 代表跨越 180 度經線，保留原值、不得排序經度。
 * @returns {{ north: number, south: number, east: number, west: number, zoom: number } | null}
 */
export function normalizeViewport(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { north, south, east, west } = raw;
  if (![north, south, east, west].every(isFiniteNumber)) return null;
  const zoom = normalizeZoom(raw.zoom);
  if (zoom == null) return null;
  const n = quantize(north);
  const s = quantize(south);
  const e = quantize(east);
  const w = quantize(west);
  if (s < -90 || n > 90 || s >= n) return null;
  if (w < -180 || w > 180 || e < -180 || e > 180) return null;
  if (e === w) return null;
  return { north: n, south: s, east: e, west: w, zoom };
}

export function viewportCrossesDateline(viewport) {
  return Boolean(viewport) && viewport.east < viewport.west;
}

/** 穩定查詢 key：north／south／east／west／zoom／kind。無效 viewport 回傳 null。 */
export function viewportQueryKey(rawViewport, kind, timeRange = 'ALL') {
  const viewport = normalizeViewport(rawViewport);
  if (!viewport) return null;
  return [
    normalizeSocialMapKind(kind),
    normalizeSocialMapTimeRange(timeRange),
    `z${viewport.zoom}`,
    viewport.north,
    viewport.south,
    viewport.east,
    viewport.west,
  ].join('|');
}

/** 轉成地點聚合查詢參數；無效 viewport 回傳 null（不得送出請求）。 */
export function buildSocialMapLocationsQuery(rawViewport, kind, timeRange = 'ALL') {
  const viewport = normalizeViewport(rawViewport);
  if (!viewport) return null;
  return {
    north: viewport.north,
    south: viewport.south,
    east: viewport.east,
    west: viewport.west,
    zoom: viewport.zoom,
    kind: normalizeSocialMapKind(kind),
    timeRange: normalizeSocialMapTimeRange(timeRange),
    limit: SOCIAL_MAP_VIEWPORT_LIMIT,
  };
}

/** 從 google.maps.Map 讀取目前可視範圍；地圖尚未有 bounds 時回傳 null。 */
export function viewportFromGoogleMap(map) {
  const bounds = map?.getBounds?.();
  if (!bounds) return null;
  const ne = bounds.getNorthEast?.();
  const sw = bounds.getSouthWest?.();
  if (!ne || !sw) return null;
  return normalizeViewport({
    north: ne.lat(),
    south: sw.lat(),
    east: ne.lng(),
    west: sw.lng(),
    zoom: map.getZoom?.(),
  });
}

export function cameraFromGoogleMap(map) {
  const center = map?.getCenter?.();
  if (!center) return null;
  return { center: { lat: center.lat(), lng: center.lng() }, zoom: map.getZoom?.() };
}

function normalizeCenter(center) {
  if (!center || !isFiniteNumber(center.lat) || !isFiniteNumber(center.lng)) return null;
  if (center.lat < -90 || center.lat > 90 || center.lng < -180 || center.lng > 180) return null;
  return { lat: quantize(center.lat), lng: quantize(center.lng) };
}

function boundsBox(viewport) {
  return { north: viewport.north, south: viewport.south, east: viewport.east, west: viewport.west };
}

function safeStorage(storage) {
  if (storage !== undefined) return storage;
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * 讀取上次地圖可視範圍（只存地圖中心、zoom 與 bounds；不存 GPS、token 或私人地點）。
 * 結構版本不符或任何欄位無效即移除並回傳 null，由呼叫端退回台灣預設。
 */
export function readStoredCamera(storage) {
  const store = safeStorage(storage);
  if (!store) return null;
  let raw;
  try {
    raw = store.getItem(SOCIAL_MAP_VIEWPORT_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const discard = () => {
    try { store.removeItem(SOCIAL_MAP_VIEWPORT_STORAGE_KEY); } catch { /* 儲存空間不可用時忽略 */ }
    return null;
  };
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return discard();
  }
  if (!parsed || parsed.v !== SOCIAL_MAP_VIEWPORT_SCHEMA_VERSION) return discard();
  const center = normalizeCenter(parsed.center);
  const zoom = Number.isInteger(parsed.zoom) ? normalizeZoom(parsed.zoom) : null;
  const bounds = normalizeViewport({ ...(parsed.bounds || {}), zoom: parsed.zoom });
  if (!center || zoom == null || !bounds) return discard();
  if (center.lat < bounds.south || center.lat > bounds.north) return discard();
  const insideLng = viewportCrossesDateline(bounds)
    ? center.lng >= bounds.west || center.lng <= bounds.east
    : center.lng >= bounds.west && center.lng <= bounds.east;
  if (!insideLng) return discard();
  return { source: 'LAST_VIEWPORT', center, zoom, bounds: boundsBox(bounds) };
}

export function writeStoredCamera(camera, viewport, storage) {
  const store = safeStorage(storage);
  if (!store) return false;
  const center = normalizeCenter(camera?.center);
  const bounds = normalizeViewport(viewport);
  if (!center || !bounds) return false;
  try {
    store.setItem(SOCIAL_MAP_VIEWPORT_STORAGE_KEY, JSON.stringify({
      v: SOCIAL_MAP_VIEWPORT_SCHEMA_VERSION,
      center,
      zoom: bounds.zoom,
      bounds: boundsBox(bounds),
    }));
    return true;
  } catch {
    return false;
  }
}

/** 初始鏡頭：上次可視範圍優先，否則台灣預設。 */
export function resolveInitialCamera(storage) {
  return readStoredCamera(storage) || TAIWAN_DEFAULT_CAMERA;
}

/** locationId 必須維持原始十進位字串；number 只接受 safe integer，避免精度遺失的 id 進入 UI。 */
export function normalizeLocationId(value) {
  if (typeof value === 'string') {
    const text = value.trim();
    return /^[1-9]\d*$/.test(text) ? text : null;
  }
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return String(value);
  return null;
}

/** matchingPostCount 可能是字串（long 以字串序列化）；超過 safe integer 的數字只用於顯示上限。 */
export function normalizeMatchingPostCount(value) {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!/^\d+$/.test(text)) return null;
    if (text.length > 15) return Number.MAX_SAFE_INTEGER;
    return Number(text);
  }
  return null;
}

/** 顯示用上限：>=100 顯示 99+；不得把截斷後文字當成數值使用。 */
export function formatMarkerCount(count) {
  if (!Number.isFinite(count) || count < 0) return '0';
  return count > SOCIAL_MAP_COUNT_DISPLAY_CAP ? `${SOCIAL_MAP_COUNT_DISPLAY_CAP}+` : String(count);
}

function normalizeCoordinate(value, limit) {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (!isFiniteNumber(number) || Math.abs(number) > limit) return null;
  return number;
}

/** 單一地點聚合 → 地圖群組（一個 locationId 一個 marker）。 */
export function toSocialMapLocationGroup(location) {
  const locationId = normalizeLocationId(location?.locationId);
  const latitude = normalizeCoordinate(location?.latitude, 90);
  const longitude = normalizeCoordinate(location?.longitude, 180);
  const count = normalizeMatchingPostCount(location?.matchingPostCount);
  if (!locationId || latitude == null || longitude == null || count == null) return null;
  const locationName = String(location?.displayName || '').trim() || '未命名地點';
  const countLabel = formatMarkerCount(count);
  return {
    key: locationId,
    locationId,
    locationName,
    latitude,
    longitude,
    count,
    countLabel,
    label: `${locationName} 的 ${countLabel} 則貼文`,
    postKinds: Array.isArray(location?.postKinds) ? location.postKinds.map(String) : [],
    latestPublishedAt: location?.latestPublishedAt || null,
  };
}

/** 地點聚合結果不完整的原因（backend contract）；不接受其他值。 */
export const SOCIAL_MAP_TRUNCATION_REASON = Object.freeze({
  NONE: 'NONE',
  RESULT_LIMIT: 'RESULT_LIMIT',
  CANDIDATE_BUDGET: 'CANDIDATE_BUDGET',
});

const TRUNCATION_REASONS = new Set(Object.values(SOCIAL_MAP_TRUNCATION_REASON));

/** 回應不符最新 contract；交由既有錯誤狀態處理，不得當成完整結果顯示。 */
export class SocialMapContractError extends Error {
  constructor(detail) {
    super(`social map contract mismatch: ${detail}`);
    this.name = 'SocialMapContractError';
  }
}

/**
 * 只接受最新 contract：truncated 必須是 boolean、truncationReason 必須是合法值，
 * 且 truncated=false ⇔ NONE。缺少或不一致一律視為 contract mismatch，不做舊版推測。
 */
function readTruncation(payload) {
  const { truncated, truncationReason } = payload || {};
  if (typeof truncated !== 'boolean') throw new SocialMapContractError('truncated');
  if (!TRUNCATION_REASONS.has(truncationReason)) throw new SocialMapContractError('truncationReason');
  if (truncated !== (truncationReason !== SOCIAL_MAP_TRUNCATION_REASON.NONE)) {
    throw new SocialMapContractError('truncated/truncationReason');
  }
  return { truncated, truncationReason };
}

/** 正規化地點聚合回應；同一 locationId 只保留第一筆。 */
export function normalizeSocialMapLocations(payload) {
  const truncation = readTruncation(payload);
  const rows = Array.isArray(payload?.locations) ? payload.locations : [];
  const seen = new Set();
  const groups = [];
  rows.forEach((row) => {
    const group = toSocialMapLocationGroup(row);
    if (!group || seen.has(group.key)) return;
    seen.add(group.key);
    groups.push(group);
  });
  return {
    groups,
    truncated: truncation.truncated,
    truncationReason: truncation.truncationReason,
    generatedAt: typeof payload?.generatedAt === 'string' ? payload.generatedAt : null,
  };
}

/** 聚焦地點快照：與 marker 集合分開保存，viewport 結果變動時不會被靜默替換。 */
export function focusedLocationSnapshot(source) {
  if (!source) return null;
  const locationId = normalizeLocationId(source.locationId ?? source.key);
  const latitude = normalizeCoordinate(source.latitude, 90);
  const longitude = normalizeCoordinate(source.longitude, 180);
  if (!locationId || latitude == null || longitude == null) return null;
  const count = Number.isFinite(source.count) ? source.count : null;
  return {
    key: locationId,
    locationId,
    locationName: String(source.locationName || source.displayName || '').trim() || '未命名地點',
    latitude,
    longitude,
    count,
    countLabel: count != null ? formatMarkerCount(count) : null,
  };
}
